import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { documentService } from '../api/services';
import useApi from '../hooks/useApi';
import {
  FileText, Download, ExternalLink, Search,
  BookOpen, Briefcase, Building2, Globe, Shield,
  ChevronDown, AlertCircle, FolderOpen, Loader2,
} from 'lucide-react';

const CATEGORY_META = {
  REGISTRATION: { label: 'Business Registration',        icon: Building2, color: 'text-forest-700 bg-forest-50 border-forest-100' },
  LICENSING:    { label: 'Tourism Licensing & Permits',   icon: Shield,    color: 'text-blue-700 bg-blue-50 border-blue-100'       },
  TAX:          { label: 'Tax & Financial Compliance',    icon: Briefcase, color: 'text-amber-700 bg-amber-50 border-amber-100'    },
  LEGAL:        { label: 'Legal & Contracts',             icon: BookOpen,  color: 'text-purple-700 bg-purple-50 border-purple-100' },
  DIGITAL:      { label: 'Digital & Online Presence',     icon: Globe,     color: 'text-green-700 bg-green-50 border-green-100'    },
  OTHER:        { label: 'Other Documents',               icon: FolderOpen,color: 'text-forest-500 bg-cream-50 border-cream-200'   },
};

const TYPE_STYLES = {
  PDF:   'bg-rust-50 text-rust-600 border border-rust-200',
  DOCX:  'bg-blue-50 text-blue-600 border border-blue-200',
  XLSX:  'bg-green-50 text-green-700 border border-green-200',
  IMAGE: 'bg-amber-50 text-amber-700 border border-amber-200',
  OTHER: 'bg-cream-100 text-forest-500 border border-cream-200',
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentsPage = () => {
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);

  const { data, loading, error } = useApi(() => documentService.getAll(), []);
  const allDocs = data?.documents ?? [];

  // Group by category
  const grouped = allDocs.reduce((acc, doc) => {
    const cat = doc.category || 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  // Filter by search
  const filteredGrouped = Object.entries(grouped).reduce((acc, [cat, docs]) => {
    const filtered = search
      ? docs.filter(d =>
          d.title.toLowerCase().includes(search.toLowerCase()) ||
          (d.description || '').toLowerCase().includes(search.toLowerCase())
        )
      : docs;
    if (filtered.length) acc[cat] = filtered;
    return acc;
  }, {});

  const categories = Object.keys(filteredGrouped);

  return (
    <DashboardLayout title="Documents">
      <div className="max-w-3xl space-y-6">

        <p className="text-sm text-forest-500">
          Essential documents, templates and guides for registering and running your business.
        </p>

        {/* Search */}
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
          <input type="text" placeholder="Search documents..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white" />
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-forest-500 text-sm py-8 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading documents...
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} /> Could not load documents.
          </div>
        )}

        {!loading && !error && categories.length === 0 && (
          <div className="text-center py-16">
            <FolderOpen size={36} className="text-forest-200 mx-auto mb-3" />
            <p className="font-serif text-xl text-forest-600">
              {search ? 'No documents match your search' : 'No documents available yet'}
            </p>
            <p className="text-sm text-forest-400 mt-1">
              {search ? 'Try a different search term' : 'Check back soon — the admin will upload documents here'}
            </p>
          </div>
        )}

        {/* Category groups */}
        {categories.map(cat => {
          const meta  = CATEGORY_META[cat] ?? CATEGORY_META.OTHER;
          const Icon  = meta.icon;
          const docs  = filteredGrouped[cat];
          const isOpen = expanded === cat || !!search;

          return (
            <div key={cat} className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <button
                onClick={() => setExpanded(isOpen && !search ? null : cat)}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-cream-50 transition-colors text-left"
              >
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center border flex-shrink-0 ${meta.color}`}>
                  <Icon size={15} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-forest-900">{meta.label}</p>
                  <p className="text-xs text-forest-400">{docs.length} document{docs.length !== 1 ? 's' : ''}</p>
                </div>
                <ChevronDown size={16} className={`text-forest-300 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOpen && (
                <div className="divide-y divide-cream-50 border-t border-cream-100">
                  {docs.map(doc => (
                    <div key={doc.id} className="px-5 py-4 flex items-start gap-4 hover:bg-cream-50/50 transition-colors">
                      <div className="w-8 h-8 bg-cream-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FileText size={14} className="text-forest-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="text-sm font-medium text-forest-900">{doc.title}</p>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${TYPE_STYLES[doc.fileType] ?? TYPE_STYLES.OTHER}`}>
                            {doc.fileType}
                          </span>
                          {doc.fileSize && (
                            <span className="text-[10px] text-forest-400">{formatSize(doc.fileSize)}</span>
                          )}
                        </div>
                        {doc.description && (
                          <p className="text-xs text-forest-500 leading-relaxed">{doc.description}</p>
                        )}
                      </div>
                      <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer"
                        className="flex-shrink-0 flex items-center gap-1.5 text-xs font-medium text-forest-600 border border-forest-200 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors">
                        {doc.fileType === 'IMAGE' ? <ExternalLink size={11} /> : <Download size={11} />}
                        {doc.fileType === 'IMAGE' ? 'View' : 'Download'}
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <p className="text-xs text-forest-400 text-center pb-4">
          Documents are provided as guides and templates only. Always consult a qualified legal or financial professional for advice specific to your situation.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;